import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/useAuth';
import { getPostAuthLandingPath } from '@/modules/auth/core/postAuthLanding';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'super_admin' | 'business_admin' | 'staff' | 'member';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { user, isLoading, isSuperAdmin, isBusinessAdmin, isStaff } = useAuth();
  const location = useLocation();
  const fallbackLandingPath = getPostAuthLandingPath({
    isSuperAdmin,
    isBusinessAdmin,
    isStaff,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (requiredRole) {
    let hasAccess = false;
    switch (requiredRole) {
      case 'super_admin':
        hasAccess = isSuperAdmin;
        break;
      case 'business_admin':
        hasAccess = isSuperAdmin || isBusinessAdmin;
        break;
      case 'staff':
        hasAccess = isSuperAdmin || isBusinessAdmin || isStaff;
        break;
      case 'member':
        hasAccess = true; // All authenticated users are members
        break;
    }

    if (!hasAccess) {
      return <Navigate to={fallbackLandingPath} replace />;
    }
  }

  return <>{children}</>;
};


